import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import { Tooltip } from 'components/Widget/Tooltip';
import { FrameType, SceneMetadata } from 'dragonmouse-shared/interfaces';
import useOnClickOutside from 'hooks/useOnClickOutside';
import { MenuItemAction } from 'menu/MenuItem';
import { useEffect, useId, useRef, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { shallowEqual, useSelector } from 'react-redux';
import { selectActiveScene, DEFAULT_CANVAS_COLOR } from 'redux/db/reducers';
import { canEdit } from 'redux/db/util';
import { alignSelectedFrames, displayViewOnlyModal, FrameSide } from 'redux/mode/util';
import { getSelectedFrameId, openCanvasLeft, openCanvasWidth } from 'redux/mode/util';
import { RootReducer, selectSelectedFrames } from 'redux/reducers';
import { useTheme } from 'styled-components';
import * as S from './styles';
import * as Island from '../styles';

const COLOR_DEBOUNCE_MS = 150;

const BottomCenter = () => {
	const theme = useTheme();
	const tooltipId = useId();
	const [showColorPicker, setShowColorPicker] = useState(false);
	const colorPickerRef = useRef<HTMLDivElement>(null);
	const [localColor, setLocalColor] = useState<string | null>(null);
	const colorDebounceRef = useRef<number | null>(null);
	const pendingColorRef = useRef<string | null>(null);

	const flushPendingColor = () => {
		if (colorDebounceRef.current === null) return;
		window.clearTimeout(colorDebounceRef.current);
		colorDebounceRef.current = null;
		const finalColor = pendingColorRef.current;
		pendingColorRef.current = null;
		if (!finalColor || !scene) return;
		forge.dbrUpdateScene({
			update: {
				id: scene.id,
				metadata: {
					...(scene.metadata as SceneMetadata),
					canvasColor: finalColor,
				},
			},
			showToast: true,
		});
	};

	useOnClickOutside(colorPickerRef, () => {
		flushPendingColor();
		setShowColorPicker(false);
	});

	// Drop the local override once the picker closes so the swatch tracks
	// scene state again.
	useEffect(() => {
		if (!showColorPicker && localColor !== null) {
			setLocalColor(null);
		}
	}, [showColorPicker, localColor]);

	useEffect(() => {
		return () => {
			if (colorDebounceRef.current !== null) {
				window.clearTimeout(colorDebounceRef.current);
			}
		};
	}, []);

	const { scene, selectedFrameId, selectedFrames, canvasLeft, canvasWidth } = useSelector(
		(r: RootReducer) => ({
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			selectedFrameId: getSelectedFrameId(r.mode.selected),
			selectedFrames: selectSelectedFrames(r),
			canvasLeft: openCanvasLeft(r.mode),
			canvasWidth: openCanvasWidth(r.mode),
		}),
		shallowEqual
	);

	const canvasCenter = canvasLeft + canvasWidth / 2;

	const selectedFrame =
		selectedFrameId && selectedFrames.length === 1
			? selectedFrames.find((f) => f.id === selectedFrameId)
			: null;

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------
	// HexColorPicker fires onChange on every drag tick. dbrUpdateScene with
	// showToast=true is a server round-trip + toast per pixel — debounce so
	// only the final hue lands in Redux/server. localColor drives the picker
	// UI synchronously while pendingColorRef holds the value for the timer.
	const onBgColorChange = (color: string) => {
		if (!scene) return;
		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}
		setLocalColor(color);
		pendingColorRef.current = color;
		if (colorDebounceRef.current !== null) {
			window.clearTimeout(colorDebounceRef.current);
		}
		colorDebounceRef.current = window.setTimeout(() => {
			colorDebounceRef.current = null;
			const finalColor = pendingColorRef.current;
			if (!finalColor) return;
			forge.dbrUpdateScene({
				update: {
					id: scene.id,
					metadata: {
						...(scene.metadata as SceneMetadata),
						canvasColor: finalColor,
					},
				},
				showToast: true,
			});
		}, COLOR_DEBOUNCE_MS);
	};

	const onPrototypePress = () => {
		if (!selectedFrame) return;
		const canOpenPrototype = selectedFrame.metadata?.type !== FrameType.IMPORT;
		if (!canOpenPrototype) return;
		if (selectedFrameId) {
			MenuItemAction.createOpenPrototype(selectedFrameId).callback();
		}
	};

	const onAlign = (direction: FrameSide) => {
		if (!scene) return;
		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}
		alignSelectedFrames(direction);
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------
	const iconSize = 14;

	// Multiple frames selected: align buttons
	if (selectedFrames.length > 1) {
		return (
			<Island.BottomCenter left={canvasCenter}>
				<Island.ControlButton
					active={true}
					buttonType={Island.ButtonType.LEFT}
					onClick={() => onAlign('left')}
					data-tooltip-id={tooltipId}
					data-tooltip-content="Align left"
				>
					<Island.IconWrapper>
						<Icon
							type={IconType.ALIGN_ITEM_LEFT}
							width={iconSize}
							height={iconSize}
							color={theme.gray[1000]}
						/>
					</Island.IconWrapper>
				</Island.ControlButton>
				<Island.VerticalLine />
				<Island.ControlButton
					active={true}
					buttonType={Island.ButtonType.CENTER}
					onClick={() => onAlign('right')}
					data-tooltip-id={tooltipId}
					data-tooltip-content="Align right"
				>
					<Island.IconWrapper>
						<Icon
							type={IconType.ALIGN_ITEM_RIGHT}
							width={iconSize}
							height={iconSize}
							color={theme.gray[1000]}
						/>
					</Island.IconWrapper>
				</Island.ControlButton>
				<Island.VerticalLine />
				<Island.ControlButton
					active={true}
					buttonType={Island.ButtonType.CENTER}
					onClick={() => onAlign('top')}
					data-tooltip-id={tooltipId}
					data-tooltip-content="Align top"
				>
					<Island.IconWrapper>
						<Icon
							type={IconType.ALIGN_ITEM_TOP}
							width={iconSize}
							height={iconSize}
							color={theme.gray[1000]}
						/>
					</Island.IconWrapper>
				</Island.ControlButton>
				<Island.VerticalLine />
				<Island.ControlButton
					active={true}
					buttonType={Island.ButtonType.RIGHT}
					onClick={() => onAlign('bottom')}
					data-tooltip-id={tooltipId}
					data-tooltip-content="Align bottom"
				>
					<Island.IconWrapper>
						<Icon
							type={IconType.ALIGN_ITEM_BOTTOM}
							width={iconSize}
							height={iconSize}
							color={theme.gray[1000]}
						/>
					</Island.IconWrapper>
				</Island.ControlButton>
				<Tooltip id={tooltipId} placement="top" />
			</Island.BottomCenter>
		);
	}

	// Single frame selected: play only
	if (selectedFrame) {
		const canOpenPrototype = selectedFrame.metadata?.type !== FrameType.IMPORT;

		return (
			<Island.BottomCenter left={canvasCenter}>
				<Island.ControlButton
					active={canOpenPrototype}
					buttonType={Island.ButtonType.SINGLE}
					onClick={onPrototypePress}
					data-tooltip-id={tooltipId}
					data-tooltip-content="Preview (P)"
				>
					<Island.IconWrapper>
						<Icon
							type={IconType.PLAY}
							width={iconSize}
							height={iconSize}
							color={canOpenPrototype ? theme.gray[1000] : theme.gray[400]}
						/>
					</Island.IconWrapper>
					<Island.Label color={canOpenPrototype ? theme.gray[1000] : theme.gray[400]}>
						Preview
					</Island.Label>
				</Island.ControlButton>
				<Tooltip id={tooltipId} placement="top" />
			</Island.BottomCenter>
		);
	}

	// Nothing selected: color picker only
	const canvasColor = localColor ?? scene?.metadata?.canvasColor ?? DEFAULT_CANVAS_COLOR;

	return (
		<Island.BottomCenter left={canvasCenter}>
			<div style={{ position: 'relative' }}>
				<Island.ControlButton
					active={true}
					buttonType={Island.ButtonType.SINGLE}
					onClick={() => setShowColorPicker(!showColorPicker)}
					data-tooltip-id={tooltipId}
					data-tooltip-content="Background color"
				>
					<S.ColorSwatch color={canvasColor} />
				</Island.ControlButton>
				{showColorPicker && (
					<S.ColorPickerPopover ref={colorPickerRef}>
						<HexColorPicker color={canvasColor} onChange={onBgColorChange} />
					</S.ColorPickerPopover>
				)}
			</div>
			<Tooltip id={tooltipId} placement="top" />
		</Island.BottomCenter>
	);
};

export default BottomCenter;
